import { useCallback, useMemo } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import { inventoryByIdSelector } from "@/domains/inventory/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryCommonHeader from "@/domains/inventory/components/InventoryCommonHeader";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";

type Props = { inventoryId: number };

export default function InventoryCharacterGarbageContainer({ inventoryId }: Props) {
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);

  const playerInfo = useAppSelector(playerInfoSelector);
  const { moveItem } = useInventoryManager();

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const onCloseHandler = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, []);

  if (!playerInfo || !inventory) return null;

  return (
    <InventoryWrapper className="max-h-full min-h-[20rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryCommonHeader onClose={onCloseHandler} ownerType={OwnerType.GarbageContainer} />
      </InventoryHeader>
      <InventoryContent className="flex items-center justify-between w-full h-[3rem]">
        <InventorySearch
          value={deferredSearchValue}
          onChange={onChangeSearchValue}
          className="w-fit"
        />
        {filterGroups.length > 0 && (
          <InventoryFilter
            groups={filterGroups}
            selectedGroups={selectedGroups}
            onSelect={onSelectHandler}
          />
        )}
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto pb-5">
        <InventoryGrid
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          disabledItems={disabledItems}
          items={inventory.items}
          onMove={moveItem}
          onMemoizedItem={() => {}}
        />
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight weight={inventory.weight} />
        {/* <Button color="gray" variant="soft" className="h-[3rem] text-14 font-medium capitalize">
          {"Забрать все"}
        </Button> */}
      </InventoryFooter>
    </InventoryWrapper>
  );
}
